import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "467",
  "slug": "how-to-spot-a-dam-from-satellite-imagery",
  "title": "How to Spot a Dam and Reservoir from Satellite Imagery",
  "excerpt": "Dams and reservoirs leave unmistakable fingerprints on the landscape. Learn the tell-tale shapes that reveal them from above, a handy skill for any satellite-guessing game.",
  "date": "2026-06-17",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1561533115-f893e69758e9?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxkYW0lMjByZXNlcnZvaXIlMjBhZXJpYWx8ZW58MXwwfHx8MTc4MTY4NDIyNnww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "tldr": "From satellite imagery, a dam shows up as a straight or gently curved line cutting across a river valley, with a large, often branching reservoir on the upstream side and a narrower channel downstream. Spotting that contrast is the key.",
  "content": [
    {
      "type": "paragraph",
      "text": "Once you learn to read satellite imagery, dams become almost impossible to miss. They are among the largest structures humans build, and they reshape entire river valleys in ways that are obvious from above. Here is how to recognize a dam and its reservoir at a glance, and why that skill pays off in any geography-guessing game."
    },
    {
      "type": "heading",
      "text": "Start With the Reservoir"
    },
    {
      "type": "paragraph",
      "text": "The easiest clue is usually not the dam itself but the lake behind it. A reservoir has a very particular shape. Because it forms when a valley is flooded, the water reaches up into every side valley and gully, producing a branching, many-fingered outline that looks a bit like a tree or a bare hand. Natural lakes tend to have smoother, more rounded shores, so a long lake with a feathered, dendritic shoreline is a strong sign that it is artificial."
    },
    {
      "type": "paragraph",
      "text": "Reservoir levels also rise and fall, which leaves a pale band of bare ground, sometimes called a bathtub ring, around the shoreline where vegetation cannot get a foothold. That ring is another giveaway you will rarely see around a natural lake."
    },
    {
      "type": "heading",
      "text": "Find the Dam Wall"
    },
    {
      "type": "paragraph",
      "text": "At one end of that branching reservoir, look for an abrupt, unnaturally straight or smoothly curved line where the water stops. That is the dam. It typically sits at the narrowest point of the valley, where engineers had the least gap to bridge. Large dams often cast a distinct shadow and may show a visible roadway running along the top, sometimes carrying an actual highway across the river."
    },
    {
      "type": "heading",
      "text": "Check Downstream"
    },
    {
      "type": "paragraph",
      "text": "The contrast between the two sides of a dam is a giveaway. Upstream lies the broad, full reservoir; immediately downstream the river is suddenly much narrower, sometimes braided across a gravel bed. You may also spot tell-tale infrastructure:"
    },
    {
      "type": "list",
      "items": [
        "A spillway, a wide ramp or channel for releasing excess water during floods.",
        "Power lines fanning out from a hydroelectric dam toward the grid.",
        "Settling or cooling ponds and a cluster of buildings at the base.",
        "A clear change in vegetation along the regulated river below."
      ]
    },
    {
      "type": "heading",
      "text": "Different Dams, Different Clues"
    },
    {
      "type": "paragraph",
      "text": "Not all dams serve the same purpose, and that affects what you see around them:"
    },
    {
      "type": "list",
      "items": [
        "Hydroelectric dams are tied to power lines and often sit in steep mountain valleys.",
        "Irrigation dams feed networks of canals that fan out across nearby farmland.",
        "Flood-control dams may sit above sprawling cities and have large basins that stay mostly empty between floods."
      ]
    },
    {
      "type": "paragraph",
      "text": "The setting often tells you the job. A wall of cables and a powerhouse points to electricity; a grid of green fields below points to irrigation."
    },
    {
      "type": "heading",
      "text": "A Quick Field Guide"
    },
    {
      "type": "paragraph",
      "text": "When you suspect a dam, run through a short checklist: a branching lake with a feathered shoreline, an abrupt straight or curved edge at one end, a sudden narrowing of the river downstream, and supporting clues like power lines or canals. Tick two or three of those and you can be confident in what you are seeing."
    },
    {
      "type": "heading",
      "text": "The Giants You Can See from Space"
    },
    {
      "type": "paragraph",
      "text": "Some dams are so large they reshape the map. China's Three Gorges Dam holds back a reservoir that stretches hundreds of kilometers up the Yangtze, while the Itaipu Dam on the border of Brazil and Paraguay and the Aswan High Dam in Egypt each created vast new lakes where dry valleys once stood. Lake Mead, behind the Hoover Dam, and Lake Kariba on the Zambezi are among the largest artificial lakes on Earth. These reservoirs are big enough to be obvious from orbit, and the abrupt, geometric edge where each one ends is one of the clearest human fingerprints on the planet."
    },
    {
      "type": "heading",
      "text": "Why It Matters for Guessing"
    },
    {
      "type": "paragraph",
      "text": "Once you have a candidate dam, follow the water upstream and downstream. Look for a reservoir, an embankment, and roads leading to the structure. Use those features together before deciding that a straight shoreline is a dam."
    }
  ]
};
